import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Check, ArrowRight, Upload, Plus, Trash2, FileText, AlertTriangle } from "lucide-react";
import Wordmark from "@/components/Wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/auth/AuthContext";
import { api, apiError } from "@/lib/api";
import { setActiveSchemeId } from "@/components/AppShell";

// Opstrait — Sprint 1 Week 2 — Item W2.2 — Full 8-step onboarding wizard.
// Single-file route. Persists progress on every "Next" via backend.

const STEP_TITLES = [
  "Sign-up", "Scheme setup", "Strata roll import", "Committee setup",
  "Compliance snapshot", "Insurance check", "Document upload", "Live + Subscribe",
];
const QLD_REG_MODULES = [
  { value: "STANDARD", label: "Standard Module" },
  { value: "ACCOMMODATION", label: "Accommodation Module" },
  { value: "COMMERCIAL", label: "Commercial Module" },
  { value: "SMALL_SCHEMES", label: "Small Schemes Module" },
  { value: "TWO_LOT", label: "Specified Two-lot Module" },
];
const DOC_CATEGORIES = [
  { value: "insurance", label: "Insurance" },
  { value: "financial", label: "Financial" },
  { value: "meetings", label: "Meetings" },
  { value: "by_laws", label: "By-laws" },
  { value: "certificates", label: "Certificates" },
  { value: "correspondence", label: "Correspondence" },
  { value: "other", label: "Other" },
];
const COMMITTEE_ROLES = [
  { value: "SECRETARY", label: "Secretary" },
  { value: "TREASURER", label: "Treasurer" },
  { value: "MEMBER", label: "Member" },
];
const OWNERSHIP_TYPES = [
  { value: "INDIVIDUAL", label: "Individual" },
  { value: "JOINT", label: "Joint" },
  { value: "COMPANY", label: "Company" },
  { value: "TRUST", label: "Trust" },
];

export default function Onboarding() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [step1, setStep1] = useState({ scheme_name: "", state: "NSW", plan_number: "", regulation_module: "STANDARD" });
  const [step2, setStep2] = useState({ total_lots: 6, building_age: "", address: "", common_property: "" });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/onboarding/progress");
        if (!alive) return;
        if (data.completed_at) {
          try {
            const r = await api.get("/schemes");
            if (r.data.length > 0) setActiveSchemeId(r.data[0].scheme_id);
          } catch (_) {}
          navigate("/dashboard", { replace: true });
          return;
        }
        setProgress(data);
        const sd = data.step_data || {};
        if (sd.step1) setStep1((p) => ({ ...p, ...sd.step1 }));
        if (sd.step2) setStep2((p) => ({ ...p, ...sd.step2 }));
      } catch (e) {
        toast.error(apiError(e));
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [navigate]);

  const goToStep = (n) => setProgress((p) => p ? { ...p, current_step: n } : p);

  const saveStep1 = async () => {
    if (!step1.scheme_name.trim() || !step1.plan_number.trim()) {
      toast.error("Scheme name and plan number are required"); return;
    }
    setSubmitting(true);
    try {
      const body = { ...step1 };
      if (body.state !== "QLD") body.regulation_module = null;
      const { data } = await api.post("/onboarding/step/1", body);
      setProgress(data); toast.success("Saved — moving to scheme setup");
    } catch (e) { toast.error(apiError(e)); } finally { setSubmitting(false); }
  };

  const saveStep2 = async () => {
    if (!step2.address.trim() || !step2.total_lots) {
      toast.error("Total lots and address are required"); return;
    }
    setSubmitting(true);
    try {
      const body = {
        total_lots: Number(step2.total_lots),
        building_age: step2.building_age ? Number(step2.building_age) : null,
        address: step2.address, common_property: step2.common_property || "",
      };
      const { data } = await api.post("/onboarding/step/2", body);
      setProgress(data); toast.success("Saved — strata roll is next");
    } catch (e) { toast.error(apiError(e)); } finally { setSubmitting(false); }
  };

  const saveAndExit = async () => {
    try { await api.post("/onboarding/save-exit"); } catch (_) {}
    if (progress?.scheme_id) {
      setActiveSchemeId(progress.scheme_id);
      navigate("/dashboard");
    } else { logout(); navigate("/login"); }
  };

  if (loading || !progress) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)", color: "var(--slate)" }}>Loading your onboarding…</div>;
  }
  const current = progress.current_step || 1;
  const schemeState = step1.state || progress.step_data?.step1?.state || "NSW";

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }} data-testid="onboarding-wizard">
      <header className="border-b" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Wordmark />
          <button className="text-xs font-semibold underline" style={{ color: "var(--slate)" }}
            onClick={saveAndExit} data-testid="onboarding-save-exit">Save &amp; exit</button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-10 lg:py-14">
        <StepIndicator current={current} maxStep={progress.max_step || 8} />
        <div className="mt-10">
          {current === 1 && (
            <StepCard n={1} title="Tell us about the scheme" hint="We use the state to apply the correct legislation rules.">
              <Step1Form value={step1} onChange={setStep1}/>
              <FooterButtons onNext={saveStep1} disabled={submitting} nextLabel="Save &amp; continue" testidNext="onboarding-step1-next"/>
            </StepCard>
          )}
          {current === 2 && (
            <StepCard n={2} title="Scheme setup" hint="Define the building. Per-lot entitlements default to 1 each — refine on Settings later.">
              <Step2Form value={step2} onChange={setStep2}/>
              <FooterButtons onBack={() => goToStep(1)} onNext={saveStep2} disabled={submitting}
                nextLabel="Save &amp; continue" testidNext="onboarding-step2-next" testidBack="onboarding-step2-back"/>
            </StepCard>
          )}
          {current === 3 && <Step3Panel progress={progress} setProgress={setProgress} onBack={() => goToStep(2)}/>}
          {current === 4 && <Step4Panel schemeState={schemeState} progress={progress} setProgress={setProgress} onBack={() => goToStep(3)}/>}
          {current === 5 && <Step5Panel progress={progress} setProgress={setProgress} onBack={() => goToStep(4)}/>}
          {current === 6 && <Step6Panel progress={progress} setProgress={setProgress} onBack={() => goToStep(5)}/>}
          {current === 7 && <Step7Panel progress={progress} setProgress={setProgress} onBack={() => goToStep(6)}/>}
          {current === 8 && <Step8Panel progress={progress} setProgress={setProgress} onBack={() => goToStep(7)} navigate={navigate} saveAndExit={saveAndExit}/>}
        </div>
      </main>
    </div>
  );
}

// =================== shared subcomponents ===================
function StepIndicator({ current, maxStep }) {
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="step-indicator">
      {Array.from({ length: maxStep }, (_, i) => i + 1).map((n) => {
        const done = n < current; const active = n === current;
        return (
          <div key={n} className="flex items-center gap-1.5 text-[11px]"
            data-testid={`step-pip-${n}`} data-state={done ? "done" : active ? "active" : "pending"}>
            <div className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs"
              style={{ background: done ? "var(--success)" : active ? "var(--blue)" : "var(--bg)",
                color: done || active ? "var(--white)" : "var(--slate)",
                border: `1px solid ${done ? "var(--success)" : active ? "var(--blue)" : "var(--border)"}` }}>
              {done ? <Check size={14}/> : n}
            </div>
            <span className="hidden md:inline" style={{ color: active ? "var(--navy)" : "var(--slate)" }}>{STEP_TITLES[n - 1]}</span>
            {n < maxStep && <div className="w-4 h-px" style={{ background: "var(--border)" }}/>}
          </div>
        );
      })}
    </div>
  );
}
function StepCard({ n, title, hint, children }) {
  return (
    <section className="bg-white rounded-xl border p-8 lg:p-10" style={{ borderColor: "var(--border)" }} data-testid={`step-${n}-card`}>
      <div className="mb-6">
        <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--blue)" }}>Step {n} of 8</div>
        <h1 className="mt-1 text-2xl lg:text-3xl font-bold" style={{ color: "var(--navy)" }}>{title}</h1>
        {hint && <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--slate)" }}>{hint}</p>}
      </div>
      {children}
    </section>
  );
}
function FooterButtons({ onBack, onNext, disabled, nextLabel, testidNext, testidBack, nextVariant }) {
  return (
    <div className="mt-8 flex items-center justify-between gap-3">
      {onBack ? <Button variant="ghost" onClick={onBack} disabled={disabled} data-testid={testidBack}><ChevronLeft size={16} className="mr-1"/> Back</Button> : <div/>}
      <Button onClick={onNext} disabled={disabled} data-testid={testidNext}
        style={{ background: nextVariant === "secondary" ? "var(--slate)" : "var(--blue)" }}>
        {nextLabel} <ArrowRight size={16} className="ml-1"/>
      </Button>
    </div>
  );
}
function Field({ label, required, hint, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--slate)" }}>
        {label} {required && <span style={{ color: "var(--alert)" }}>*</span>}
      </Label>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-[11px]" style={{ color: "var(--tagline-grey)" }}>{hint}</p>}
    </div>
  );
}

// =================== Step 1 & 2 forms ===================
function Step1Form({ value, onChange }) {
  const set = (k) => (e) => onChange((p) => ({ ...p, [k]: typeof e === "object" && e?.target ? e.target.value : e }));
  return (
    <div className="space-y-5 max-w-xl">
      <Field label="Scheme name" required>
        <Input value={value.scheme_name} onChange={set("scheme_name")} placeholder="e.g. Sunset Vista Apartments" data-testid="step1-scheme-name"/>
      </Field>
      <Field label="State" required>
        <Select value={value.state} onValueChange={set("state")}>
          <SelectTrigger data-testid="step1-state-select"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="NSW">New South Wales</SelectItem>
            <SelectItem value="QLD">Queensland</SelectItem>
            <SelectItem value="VIC">Victoria</SelectItem>
            <SelectItem value="WA">Western Australia</SelectItem>
            <SelectItem value="SA">South Australia</SelectItem>
            <SelectItem value="TAS">Tasmania</SelectItem>
            <SelectItem value="ACT">Australian Capital Territory</SelectItem>
            <SelectItem value="NT">Northern Territory</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      {!["NSW", "QLD"].includes(value.state) && (
        <div
          className="rounded-md p-3 text-xs"
          style={{ background: "#fef3c7", color: "#78350f", borderLeft: "3px solid #f59e0b" }}
          data-testid="jurisdiction-not-live-advisory"
        >
          <strong>Heads up:</strong> This jurisdiction is not yet enabled on Opstrait. You can
          create your scheme, but regulated actions (compliance items, meetings, levies, by-laws)
          will be blocked until your jurisdiction is verified with a strata lawyer and an
          administrator signs it off.
        </div>
      )}
      {value.state === "QLD" && (
        <Field label="Regulation module" required hint="Determines QLD notice periods and quorum rules.">
          <Select value={value.regulation_module} onValueChange={set("regulation_module")}>
            <SelectTrigger data-testid="step1-regmod-select"><SelectValue/></SelectTrigger>
            <SelectContent>{QLD_REG_MODULES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      )}
      <Field label="Plan number" required hint="The Strata Plan number (e.g. SP12345, BUP12345).">
        <Input value={value.plan_number} onChange={set("plan_number")} placeholder="SP12345" data-testid="step1-plan-number"/>
      </Field>
    </div>
  );
}
function Step2Form({ value, onChange }) {
  const set = (k) => (e) => onChange((p) => ({ ...p, [k]: typeof e === "object" && e?.target ? e.target.value : e }));
  return (
    <div className="space-y-5 max-w-xl">
      <Field label="Total lots" required hint="Lots created with default entitlement 1 — refine on Settings.">
        <Input type="number" min={1} max={2000} value={value.total_lots} onChange={set("total_lots")} data-testid="step2-total-lots"/>
      </Field>
      <Field label="Year built" hint="Optional — helps the AI tune compliance prompts.">
        <Input type="number" min={1800} max={2100} value={value.building_age} onChange={set("building_age")} placeholder="2008" data-testid="step2-building-age"/>
      </Field>
      <Field label="Building address" required>
        <Input value={value.address} onChange={set("address")} placeholder="42 Smith St, Surry Hills NSW 2010" data-testid="step2-address"/>
      </Field>
      <Field label="Common property" hint="Shared amenities — pool, gym, lifts, parking.">
        <Textarea rows={3} value={value.common_property} onChange={set("common_property")} placeholder="Lap pool, gym, courtyard garden, 2 lifts, underground secure parking…" data-testid="step2-common-property"/>
      </Field>
    </div>
  );
}

// =================== Step 3 — Strata roll import ===================
function Step3Panel({ progress, setProgress, onBack }) {
  const [mode, setMode] = useState("csv");
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [manualRows, setManualRows] = useState([
    { lot_number: 1, owner_email: "", owner_name: "", primary_contact_phone: "", mailing_address: "", ownership_type: "INDIVIDUAL" },
  ]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/onboarding/strata-roll/preview");
        if (data) { setPreview(data); setMode("csv"); }
      } catch (_) {}
    })();
  }, []);

  const uploadCsv = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData(); form.append("file", file);
      const { data } = await api.post("/onboarding/strata-roll/preview", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      toast.success(`Parsed ${data.summary.total} rows — ${data.summary.valid} valid, ${data.summary.errors} with errors.`);
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/onboarding/strata-roll/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "opstrai-strata-roll-template.csv";
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const commitCsv = async () => {
    if (!preview || preview.summary.valid === 0) { toast.error("No valid rows to commit"); return; }
    setUploading(true);
    try {
      const { data } = await api.post("/onboarding/strata-roll/commit", {});
      toast.success(`Invited ${data.result.created} owners. ${data.result.skipped} skipped.`);
      const pr = await api.get("/onboarding/progress");
      setProgress(pr.data);
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const commitManual = async () => {
    const validRows = manualRows.filter((r) => r.owner_email && r.owner_name);
    if (validRows.length === 0) { toast.error("Add at least one complete row"); return; }
    setUploading(true);
    try {
      const { data } = await api.post("/onboarding/strata-roll/manual", { rows: validRows });
      toast.success(`Invited ${data.result.created} owners. ${data.result.skipped} skipped.`);
      const pr = await api.get("/onboarding/progress");
      setProgress(pr.data);
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const addRow = () => setManualRows((p) => [...p, {
    lot_number: (p[p.length - 1]?.lot_number || 0) + 1, owner_email: "", owner_name: "", primary_contact_phone: "", mailing_address: "", ownership_type: "INDIVIDUAL",
  }]);
  const removeRow = (i) => setManualRows((p) => p.filter((_, idx) => idx !== i));
  const setRow = (i, k, v) => setManualRows((p) => p.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  return (
    <StepCard n={3} title="Strata roll import" hint="Add the owners. Upload a CSV or enter rows manually. Each owner gets an invite email.">
      <div className="flex gap-2 mb-5">
        <button onClick={() => setMode("csv")} data-testid="step3-tab-csv"
          className="px-4 py-2 rounded-md text-xs font-semibold"
          style={{ background: mode === "csv" ? "var(--blue)" : "var(--bg)", color: mode === "csv" ? "var(--white)" : "var(--slate)", border: "1px solid var(--border)" }}>Upload CSV</button>
        <button onClick={() => setMode("manual")} data-testid="step3-tab-manual"
          className="px-4 py-2 rounded-md text-xs font-semibold"
          style={{ background: mode === "manual" ? "var(--blue)" : "var(--bg)", color: mode === "manual" ? "var(--white)" : "var(--slate)", border: "1px solid var(--border)" }}>Enter manually</button>
        <button onClick={downloadTemplate} className="ml-auto text-xs underline" style={{ color: "var(--blue)" }} data-testid="step3-download-template">Download template</button>
      </div>
      {mode === "csv" ? (
        <div>
          <div className="border-2 border-dashed rounded-lg p-6 text-center" style={{ borderColor: "var(--border)" }} data-testid="step3-csv-dropzone">
            <Upload size={28} className="mx-auto mb-2" style={{ color: "var(--slate)" }}/>
            <p className="text-sm" style={{ color: "var(--slate)" }}>Drop your CSV here, or</p>
            <input ref={fileInputRef} type="file" accept=".csv" hidden onChange={(e) => uploadCsv(e.target.files?.[0])} data-testid="step3-csv-input"/>
            <button onClick={() => fileInputRef.current?.click()} className="mt-2 text-sm font-semibold underline" style={{ color: "var(--blue)" }} data-testid="step3-csv-browse">Browse files</button>
          </div>
          {preview && (
            <div className="mt-5" data-testid="step3-preview">
              <div className="flex items-center gap-3 mb-3 text-xs">
                <span className="font-mono" data-testid="step3-preview-summary">Total {preview.summary.total} · <strong style={{ color: "var(--success)" }}>{preview.summary.valid} valid</strong> · <strong style={{ color: "var(--alert)" }}>{preview.summary.errors} errors</strong></span>
              </div>
              <div className="border rounded-md overflow-hidden" style={{ borderColor: "var(--border)" }}>
                <table className="w-full text-xs">
                  <thead style={{ background: "var(--bg)" }}>
                    <tr><Th>Lot</Th><Th>Email</Th><Th>Name</Th><Th>Type</Th><Th>Issues</Th></tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={i} data-testid={`step3-row-${i}`} style={{ borderTop: "1px solid var(--border)", background: r.errors.length ? "rgba(220, 53, 69, 0.06)" : undefined }}>
                        <Td>{r.lot_number}</Td><Td>{r.owner_email || "—"}</Td><Td>{r.owner_name || "—"}</Td><Td>{r.ownership_type}</Td>
                        <Td>{r.errors.length === 0 ? <span style={{ color: "var(--success)" }}>OK</span> :
                          <span style={{ color: "var(--alert)" }} data-testid={`step3-row-${i}-errors`}>{r.errors.join("; ")}</span>}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <FooterButtons onBack={onBack} onNext={commitCsv} disabled={uploading || !preview || preview.summary.valid === 0}
            nextLabel="Continue with valid rows" testidNext="step3-csv-commit" testidBack="step3-back"/>
        </div>
      ) : (
        <div data-testid="step3-manual">
          <div className="space-y-2">
            {manualRows.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`step3-manual-row-${i}`}>
                <Input type="number" className="col-span-1" value={r.lot_number} onChange={(e) => setRow(i, "lot_number", Number(e.target.value))} data-testid={`step3-manual-lot-${i}`}/>
                <Input className="col-span-3" placeholder="Email" value={r.owner_email} onChange={(e) => setRow(i, "owner_email", e.target.value)} data-testid={`step3-manual-email-${i}`}/>
                <Input className="col-span-3" placeholder="Name" value={r.owner_name} onChange={(e) => setRow(i, "owner_name", e.target.value)} data-testid={`step3-manual-name-${i}`}/>
                <Input className="col-span-2" placeholder="Phone" value={r.primary_contact_phone} onChange={(e) => setRow(i, "primary_contact_phone", e.target.value)}/>
                <select className="col-span-2 border rounded-md text-xs p-2" value={r.ownership_type} onChange={(e) => setRow(i, "ownership_type", e.target.value)}>
                  {OWNERSHIP_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <button onClick={() => removeRow(i)} className="col-span-1 text-right" data-testid={`step3-manual-remove-${i}`} aria-label="remove"><Trash2 size={14} style={{ color: "var(--alert)" }}/></button>
              </div>
            ))}
          </div>
          <button onClick={addRow} className="mt-3 text-xs font-semibold flex items-center gap-1" style={{ color: "var(--blue)" }} data-testid="step3-manual-add"><Plus size={14}/> Add another row</button>
          <FooterButtons onBack={onBack} onNext={commitManual} disabled={uploading}
            nextLabel="Send invites &amp; continue" testidNext="step3-manual-commit" testidBack="step3-back"/>
        </div>
      )}
    </StepCard>
  );
}
function Th({ children }) { return <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-[10px]" style={{ color: "var(--slate)" }}>{children}</th>; }
function Td({ children }) { return <td className="px-3 py-2 align-top" style={{ color: "var(--navy)" }}>{children}</td>; }

// =================== Step 4 — Committee setup ===================
function Step4Panel({ schemeState, progress, setProgress, onBack }) {
  const [rows, setRows] = useState([
    { email: "", name: "", committee_role: "MEMBER", training_completed_at: "", training_provider: "" },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const max = schemeState === "NSW" ? 9 : 15;
  const addRow = () => rows.length < max && setRows((p) => [...p, { email: "", name: "", committee_role: "MEMBER", training_completed_at: "", training_provider: "" }]);
  const removeRow = (i) => setRows((p) => p.filter((_, idx) => idx !== i));
  const setRow = (i, k, v) => setRows((p) => p.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const submit = async () => {
    const filled = rows.filter((r) => r.email && r.email.trim());
    if (filled.length === 0) { toast.error("Add at least one committee member"); return; }
    setSubmitting(true);
    try {
      const payload = filled.map((r) => ({
        email: r.email.trim().toLowerCase(),
        name: r.name || null,
        committee_role: r.committee_role,
        training_completed_at: schemeState === "NSW" && r.training_completed_at ? r.training_completed_at : null,
        training_provider: schemeState === "NSW" && r.training_provider ? r.training_provider : null,
      }));
      const { data } = await api.post("/onboarding/step/4", { members: payload });
      toast.success(`Invited ${data.created} committee members. ${data.skipped} skipped.`);
      const pr = await api.get("/onboarding/progress");
      setProgress(pr.data);
    } catch (e) { toast.error(apiError(e)); } finally { setSubmitting(false); }
  };
  return (
    <StepCard n={4} title="Committee setup" hint={`Add committee members. ${schemeState === "NSW" ? "NSW allows up to 9 committee members." : "QLD: up to 15."} Each gets an invite email.`}>
      {schemeState === "NSW" && (
        <p className="text-xs italic mb-3" style={{ color: "var(--tagline-grey)" }}>
          If you have training certificates handy, you can record them now — otherwise add them later in /scheme/members.
        </p>
      )}
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={i} className="border rounded-md p-3" style={{ borderColor: "var(--border)" }} data-testid={`step4-row-${i}`}>
            <div className="grid grid-cols-12 gap-2 items-center">
              <Input className="col-span-4" placeholder="email@example.com" value={r.email} onChange={(e) => setRow(i, "email", e.target.value)} data-testid={`step4-email-${i}`}/>
              <Input className="col-span-3" placeholder="Name" value={r.name} onChange={(e) => setRow(i, "name", e.target.value)} data-testid={`step4-name-${i}`}/>
              <select className="col-span-4 border rounded-md text-xs p-2" value={r.committee_role} onChange={(e) => setRow(i, "committee_role", e.target.value)} data-testid={`step4-role-${i}`}>
                {COMMITTEE_ROLES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button onClick={() => removeRow(i)} className="col-span-1 text-right" data-testid={`step4-remove-${i}`} aria-label="remove"><Trash2 size={14} style={{ color: "var(--alert)" }}/></button>
            </div>
            {schemeState === "NSW" && (
              <div className="grid grid-cols-12 gap-2 mt-2">
                <Input type="date" className="col-span-4" placeholder="Training completed" value={r.training_completed_at} onChange={(e) => setRow(i, "training_completed_at", e.target.value)} data-testid={`step4-training-date-${i}`}/>
                <Input className="col-span-7" placeholder="Training provider (optional)" value={r.training_provider} onChange={(e) => setRow(i, "training_provider", e.target.value)} data-testid={`step4-training-provider-${i}`}/>
              </div>
            )}
          </div>
        ))}
      </div>
      <button onClick={addRow} className="mt-3 text-xs font-semibold flex items-center gap-1" style={{ color: "var(--blue)" }} disabled={rows.length >= max} data-testid="step4-add-row"><Plus size={14}/> Add member</button>
      <FooterButtons onBack={onBack} onNext={submit} disabled={submitting} nextLabel="Send invites &amp; continue" testidNext="step4-next" testidBack="step4-back"/>
    </StepCard>
  );
}

// =================== Step 5 — Compliance snapshot ===================
function Step5Panel({ progress, setProgress, onBack }) {
  const [items, setItems] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/schemes/${progress.scheme_id}/compliance`);
        setItems(data);
      } catch (_) { setItems([]); }
    })();
  }, [progress.scheme_id]);
  const top3 = (items || []).slice().sort((a, b) => (a.due_date || "").localeCompare(b.due_date || "")).slice(0, 3);
  const cont = async () => {
    setSubmitting(true);
    try { await api.post("/onboarding/step/5"); const pr = await api.get("/onboarding/progress"); setProgress(pr.data); }
    catch (e) { toast.error(apiError(e)); } finally { setSubmitting(false); }
  };
  return (
    <StepCard n={5} title="Compliance snapshot" hint="We've already set up your scheme's compliance items. Here's what's due first.">
      <div className="text-center py-4">
        <div className="text-5xl font-bold" style={{ color: "var(--blue)" }} data-testid="step5-count">{items ? items.length : "…"}</div>
        <div className="mt-1 text-sm" style={{ color: "var(--slate)" }}>compliance items auto-generated for your scheme</div>
      </div>
      {top3.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--slate)" }}>Top 3 due soonest</div>
          <div className="space-y-2" data-testid="step5-top3">
            {top3.map((it, i) => (
              <div key={it.compliance_item_id || i} className="flex items-center gap-3 border rounded-md p-3" style={{ borderColor: "var(--border)" }} data-testid={`step5-item-${i}`}>
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: it.status === "overdue" ? "var(--alert)" : it.status === "due_soon" ? "var(--warning)" : "var(--blue)" }}/>
                <div className="flex-1">
                  <div className="text-sm font-semibold" style={{ color: "var(--navy)" }}>{it.title}</div>
                  <div className="text-xs" style={{ color: "var(--slate)" }}>Due {it.due_date}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <FooterButtons onBack={onBack} onNext={cont} disabled={submitting} nextLabel="Looks good — continue" testidNext="step5-next" testidBack="step5-back"/>
    </StepCard>
  );
}

// =================== Step 6 — Insurance check ===================
function Step6Panel({ progress, setProgress, onBack }) {
  const [form, setForm] = useState({
    insurer_name: "", policy_number: "", policy_type: "building",
    sum_insured: "", premium: "", period_start: "", period_end: "",
    broker_name: "", broker_contact_email: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: typeof e === "object" && e?.target ? e.target.value : e }));

  // Restore draft on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/onboarding/insurance-draft");
        if (data && data.draft) setForm((p) => ({ ...p, ...data.draft }));
      } catch (_) {}
    })();
  }, []);

  // Persist draft on blur (cheap, but not every keystroke)
  const saveDraft = async () => {
    try { await api.post("/onboarding/insurance-draft", form); } catch (_) {}
  };

  const submit = async () => {
    if (!form.insurer_name || !form.policy_number || !form.sum_insured) {
      toast.error("Insurer, policy number, and sum insured are required"); return;
    }
    setSubmitting(true);
    try {
      const body = {
        insurer_name: form.insurer_name, policy_number: form.policy_number,
        policy_type: form.policy_type,
        sum_insured: Number(form.sum_insured), premium: Number(form.premium || 0),
        period_start: form.period_start, period_end: form.period_end,
        broker_name: form.broker_name || null, broker_contact_email: form.broker_contact_email || null,
      };
      const policyRes = await api.post(`/schemes/${progress.scheme_id}/insurance`, body);
      await api.post("/onboarding/step/6", { policy_id: policyRes.data.policy_id });
      toast.success("Insurance policy added — compliance item auto-created.");
      const pr = await api.get("/onboarding/progress"); setProgress(pr.data);
    } catch (e) { toast.error(apiError(e)); } finally { setSubmitting(false); }
  };

  const skip = async () => {
    try { await api.post("/onboarding/step/6/skip"); const pr = await api.get("/onboarding/progress"); setProgress(pr.data); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <StepCard n={6} title="Insurance check" hint="Add your building insurance policy. We'll auto-create a renewal compliance item linked to its expiry.">
      <div className="grid grid-cols-2 gap-4 max-w-2xl" onBlur={saveDraft} data-testid="step6-form">
        <Field label="Insurer name" required><Input value={form.insurer_name} onChange={set("insurer_name")} data-testid="step6-insurer"/></Field>
        <Field label="Policy number" required><Input value={form.policy_number} onChange={set("policy_number")} data-testid="step6-policy-number"/></Field>
        <Field label="Policy type">
          <Select value={form.policy_type} onValueChange={(v) => set("policy_type")(v)}>
            <SelectTrigger data-testid="step6-policy-type"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="building">Building</SelectItem>
              <SelectItem value="public_liability">Public Liability</SelectItem>
              <SelectItem value="office_bearers">Office Bearers</SelectItem>
              <SelectItem value="workers_comp">Workers' Comp</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Sum insured ($)" required><Input type="number" value={form.sum_insured} onChange={set("sum_insured")} data-testid="step6-sum-insured"/></Field>
        <Field label="Premium ($)"><Input type="number" value={form.premium} onChange={set("premium")} data-testid="step6-premium"/></Field>
        <Field label="Period start"><Input type="date" value={form.period_start} onChange={set("period_start")} data-testid="step6-period-start"/></Field>
        <Field label="Period end"><Input type="date" value={form.period_end} onChange={set("period_end")} data-testid="step6-period-end"/></Field>
        <Field label="Broker name"><Input value={form.broker_name} onChange={set("broker_name")} data-testid="step6-broker-name"/></Field>
        <Field label="Broker email"><Input value={form.broker_contact_email} onChange={set("broker_contact_email")} data-testid="step6-broker-email"/></Field>
      </div>
      <div className="mt-4 text-xs">
        <button onClick={skip} className="underline" style={{ color: "var(--slate)" }} data-testid="step6-skip">Skip for now — I'll add insurance later</button>
      </div>
      <FooterButtons onBack={onBack} onNext={submit} disabled={submitting} nextLabel="Save policy &amp; continue" testidNext="step6-next" testidBack="step6-back"/>
    </StepCard>
  );
}

// =================== Step 7 — Document upload ===================
function Step7Panel({ progress, setProgress, onBack }) {
  const [uploads, setUploads] = useState([]); // [{file, category, status, document_id}]
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);
  const addFiles = (files) => {
    const next = Array.from(files || []).map((f) => ({ file: f, category: "other", status: "pending" }));
    setUploads((p) => [...p, ...next]);
  };
  const updateCat = (i, v) => setUploads((p) => p.map((u, idx) => idx === i ? { ...u, category: v } : u));
  const remove = (i) => setUploads((p) => p.filter((_, idx) => idx !== i));
  const uploadAll = async () => {
    setSubmitting(true);
    let ok = 0;
    for (let i = 0; i < uploads.length; i++) {
      const u = uploads[i];
      if (u.status === "done") { ok += 1; continue; }
      try {
        const form = new FormData();
        form.append("file", u.file);
        form.append("category", u.category);
        form.append("title", u.file.name);
        const { data } = await api.post(`/schemes/${progress.scheme_id}/documents`, form, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
        setUploads((p) => p.map((x, idx) => idx === i ? { ...x, status: "done", document_id: data.document_id } : x));
      } catch (e) {
        setUploads((p) => p.map((x, idx) => idx === i ? { ...x, status: "error" } : x));
      }
    }
    setSubmitting(false);
    toast.success(`Uploaded ${ok} of ${uploads.length} files`);
  };
  const cont = async () => {
    setSubmitting(true);
    try { await api.post("/onboarding/step/7", { skipped: uploads.length === 0 }); const pr = await api.get("/onboarding/progress"); setProgress(pr.data); }
    catch (e) { toast.error(apiError(e)); } finally { setSubmitting(false); }
  };
  return (
    <StepCard n={7} title="Document upload" hint="Add any policies, financials, certificates. 10MB per file. You can add more later from /scheme/documents.">
      <div className="border-2 border-dashed rounded-lg p-6 text-center" style={{ borderColor: "var(--border)" }} data-testid="step7-dropzone">
        <Upload size={28} className="mx-auto mb-2" style={{ color: "var(--slate)" }}/>
        <p className="text-sm" style={{ color: "var(--slate)" }}>Drop files or</p>
        <input ref={fileRef} type="file" multiple hidden onChange={(e) => addFiles(e.target.files)} data-testid="step7-file-input"/>
        <button onClick={() => fileRef.current?.click()} className="mt-2 text-sm font-semibold underline" style={{ color: "var(--blue)" }} data-testid="step7-browse">Browse files</button>
      </div>
      {uploads.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="step7-list">
          {uploads.map((u, i) => (
            <div key={i} className="flex items-center gap-3 border rounded-md p-2 text-xs" style={{ borderColor: "var(--border)" }} data-testid={`step7-row-${i}`}>
              <FileText size={14} style={{ color: "var(--slate)" }}/>
              <span className="flex-1 truncate" style={{ color: "var(--navy)" }}>{u.file.name}</span>
              <select className="border rounded-md text-xs p-1" value={u.category} onChange={(e) => updateCat(i, e.target.value)} data-testid={`step7-cat-${i}`}>
                {DOC_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <span style={{ color: u.status === "done" ? "var(--success)" : u.status === "error" ? "var(--alert)" : "var(--slate)" }} data-testid={`step7-status-${i}`}>{u.status}</span>
              <button onClick={() => remove(i)} aria-label="remove"><Trash2 size={14} style={{ color: "var(--alert)" }}/></button>
            </div>
          ))}
          <Button onClick={uploadAll} size="sm" disabled={submitting} className="mt-2" data-testid="step7-upload-all">Upload all</Button>
        </div>
      )}
      <FooterButtons onBack={onBack} onNext={cont} disabled={submitting} nextLabel={uploads.length ? "Continue" : "Skip for now"} testidNext="step7-next" testidBack="step7-back"/>
    </StepCard>
  );
}

// =================== Step 8 — Live + Subscribe ===================
function Step8Panel({ progress, setProgress, onBack, navigate, saveAndExit }) {
  const [submitting, setSubmitting] = useState(false);
  // Sprint 1 Week 2 closeout — auto-fire POST /step/8 on mount (idempotent).
  // Ensures completed_at is set even if user closes tab without clicking Dashboard.
  useEffect(() => {
    (async () => {
      try { await api.post("/onboarding/step/8"); } catch (_) {}
    })();
  }, []);
  const finish = async () => {
    setSubmitting(true);
    try {
      await api.post("/onboarding/step/8");
      setActiveSchemeId(progress.scheme_id);
      navigate("/dashboard");
    } catch (e) { toast.error(apiError(e)); } finally { setSubmitting(false); }
  };
  const goWithMark = async (where) => {
    try { await api.post("/onboarding/step/8"); } catch (_) {}
    setActiveSchemeId(progress.scheme_id);
    navigate(where);
  };
  return (
    <StepCard n={8} title={<>Your Opstrait is ready <span aria-hidden="true">🎉</span></>} hint="Pick how you want to start. You can subscribe later — the dashboard is open now.">
      <div className="grid md:grid-cols-3 gap-4 mt-4" data-testid="step8-cta-grid">
        <CtaCard testid="step8-cta-subscribe" title="Activate subscription" body="Set up Stripe billing to unlock unlimited owners and AI features." onClick={() => goWithMark("/scheme/billing")} secondary/>
        <CtaCard testid="step8-cta-invite" title="Invite first owners" body="Track invite acceptance and resend reminders." onClick={() => goWithMark("/scheme/members")} secondary/>
        <CtaCard testid="step8-cta-dashboard" title="Go to dashboard" body="See compliance health, meetings, and AI Q&A right now." onClick={finish} primary disabled={submitting}/>
      </div>
      <div className="mt-6 text-center">
        <button onClick={onBack} className="text-xs underline" style={{ color: "var(--slate)" }} data-testid="step8-back">Back to step 7</button>
      </div>
    </StepCard>
  );
}
function CtaCard({ title, body, onClick, primary, secondary, disabled, testid }) {
  return (
    <button onClick={onClick} disabled={disabled} className="text-left border rounded-xl p-5 transition-shadow hover:shadow-md"
      data-testid={testid}
      style={{ borderColor: primary ? "var(--blue)" : "var(--border)",
        background: primary ? "var(--blue)" : "var(--white)",
        color: primary ? "var(--white)" : "var(--navy)" }}>
      <div className="text-sm font-bold">{title}</div>
      <p className="mt-2 text-xs leading-relaxed" style={{ color: primary ? "rgba(255,255,255,0.85)" : "var(--slate)" }}>{body}</p>
      <div className="mt-3 text-xs font-semibold flex items-center gap-1">Go <ArrowRight size={12}/></div>
    </button>
  );
}
